// lib/obstacles/loadObstacles.js
import * as THREE from 'three'

/**
 * @typedef {Object} AABBCollider
 * @property {string} name - Original object name from Blender
 * @property {'OBSTACLE' | 'NO_GO'} type - Collider type
 * @property {THREE.Box3} box - World-space AABB (enlarged for rotated boxes)
 * @property {THREE.Vector3} center - World-space center of the OBB
 * @property {THREE.Vector3} size - World-space AABB size (for legacy code)
 * @property {THREE.Vector3} halfExtents - OBB half-size along its local axes
 * @property {THREE.Vector3} axisX - World-space X basis axis of the OBB
 * @property {THREE.Vector3} axisY - World-space Y basis axis of the OBB
 * @property {THREE.Vector3} axisZ - World-space Z basis axis of the OBB
 * @property {THREE.Quaternion} quaternion - World-space rotation of the OBB
 */

/**
 * Parses the obstacle scene graph and extracts AABB colliders.
 * Expects objects named OBSTACLE_* and NO_GO_* in the GLB.
 * 
 * @param {THREE.Object3D} sceneRoot - The root of the loaded GLB scene
 * @returns {{ obstacles: AABBCollider[], noGoZones: AABBCollider[] }}
 */
export function parseObstacles(sceneRoot) {
  const obstacles = []
  const noGoZones = []
  const walkables = []
  const unnamedMeshes = []

  sceneRoot.traverse((obj) => {
    if (!obj.isMesh) return

    let type = null
    if (obj.name?.startsWith('OBSTACLE_')) {
      type = 'OBSTACLE'
    } else if (obj.name?.startsWith('NO_GO_')) {
      type = 'NO_GO'
    } else if (obj.name?.startsWith('WALK_')) {
      // A surface the player can STAND ON but which blocks nobody: raised
      // floors, stair treads, platforms. Never shipped to the sim, so the
      // bird walks straight through the footprint — use this for any
      // raised ground on a route the bird still has to cross.
      type = 'WALK'
    }

    // Ensure world matrix is up to date
    obj.updateWorldMatrix(true, true)

    // World-space AABB (kept for legacy code paths)
    const box = new THREE.Box3().setFromObject(obj)
    const size = new THREE.Vector3()
    box.getSize(size)

    // --- OBB extraction ---
    // Local-space geometry bounding box
    obj.geometry.computeBoundingBox()
    const localBox = obj.geometry.boundingBox
    const localSize = new THREE.Vector3()
    const localCenter = new THREE.Vector3()
    localBox.getSize(localSize)
    localBox.getCenter(localCenter)

    // Decompose world matrix into TRS
    const position = new THREE.Vector3()
    const quaternion = new THREE.Quaternion()
    const scale = new THREE.Vector3()
    obj.matrixWorld.decompose(position, quaternion, scale)

    // OBB center: local center transformed into world space
    const center = localCenter.clone().multiply(scale).applyQuaternion(quaternion).add(position)

    // Half extents in each local axis, scaled
    const halfExtents = localSize.clone().multiplyScalar(0.5).multiply(scale)

    // World-space basis vectors of the OBB
    const axisX = new THREE.Vector3(1, 0, 0).applyQuaternion(quaternion)
    const axisY = new THREE.Vector3(0, 1, 0).applyQuaternion(quaternion)
    const axisZ = new THREE.Vector3(0, 0, 1).applyQuaternion(quaternion)

    const collider = {
      name: obj.name || 'unnamed',
      type: type || 'OBSTACLE',
      box,
      center,
      size,
      halfExtents,
      axisX,
      axisY,
      axisZ,
      quaternion,
    }

    if (type === 'OBSTACLE') {
      obstacles.push(collider)
    } else if (type === 'NO_GO') {
      noGoZones.push(collider)
    } else if (type === 'WALK') {
      walkables.push(collider)
    } else {
      // No naming convention — collect for fallback
      unnamedMeshes.push(collider)
    }
  })

  // Fallback: if no explicitly-named obstacles were found, treat every mesh as one.
  // Supports the common case where the entire GLB is dedicated to obstacle bounds.
  if (obstacles.length === 0 && noGoZones.length === 0 && unnamedMeshes.length > 0) {
    obstacles.push(...unnamedMeshes)
    console.log(`[Obstacles] No OBSTACLE_/NO_GO_ named meshes; treating all ${unnamedMeshes.length} unnamed meshes as obstacles (${walkables.length} WALK_ surfaces)`)
  } else {
    console.log(`[Obstacles] Parsed ${obstacles.length} obstacles, ${noGoZones.length} no-go zones, ${walkables.length} walkable surfaces`)
  }

  return { obstacles, noGoZones, walkables }
}

/**
 * The top of the highest WALK_ surface under (x, z), or -Infinity where
 * there is none. The viewer owns his height and took it off the terrain
 * alone, so the floor of the tube carriage, a metre up, would have drawn
 * him a metre under it (30 Oct); a visitor already stood on these.
 *
 * @param {AABBCollider[]} walkables - from parseObstacles
 * @param {number} x
 * @param {number} z
 * @returns {number}
 */
export function walkableTopAt(walkables, x, z) {
  let top = -Infinity
  for (const w of walkables || []) {
    const dx = x - w.center.x, dz = z - w.center.z
    const lx = dx * w.axisX.x + dz * w.axisX.z
    const lz = dx * w.axisZ.x + dz * w.axisZ.z
    if (Math.abs(lx) <= w.halfExtents.x && Math.abs(lz) <= w.halfExtents.z) top = Math.max(top, w.center.y + w.halfExtents.y)
  }
  return top
}

/**
 * Colliders for the blockers of the things he asked for (lib/world/wants.js),
 * in the same shape parseObstacles makes, so every consumer (the boxes the
 * sim is sent, the nav grid, the scanner) treats a market stall exactly as
 * it treats a wall from obstacles.glb. Data rather than a second GLB: no
 * load, nothing to suspend on, nothing that can fail and take the town's
 * collision with it.
 *
 * @param {{name: string, cx: number, cz: number, hx: number, hz: number, ax: number[]}[]} blocks
 */
export function collidersFromBlocks(blocks, height = 3, type = 'OBSTACLE') {
  return blocks.map((b) => {
    const theta = Math.atan2(-b.ax[1], b.ax[0])
    const quaternion = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), theta)
    const axisX = new THREE.Vector3(1, 0, 0).applyQuaternion(quaternion)
    const axisY = new THREE.Vector3(0, 1, 0)
    const axisZ = new THREE.Vector3(0, 0, 1).applyQuaternion(quaternion)
    // a block may say how high it starts and stops (y0, y1: the monorail's
    // station, 15 m up); otherwise it stands on the ground, `height` tall
    const y0 = b.y0 ?? 0, y1 = b.y1 ?? height
    const center = new THREE.Vector3(b.cx, (y0 + y1) / 2, b.cz)
    const halfExtents = new THREE.Vector3(b.hx, (y1 - y0) / 2, b.hz)
    const box = new THREE.Box3()
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
      box.expandByPoint(center.clone().addScaledVector(axisX, sx * b.hx).addScaledVector(axisZ, sz * b.hz).setY(y0))
    }
    box.max.y = y1
    const size = new THREE.Vector3(); box.getSize(size)
    return { name: b.name, type, box, center, size, halfExtents, axisX, axisY, axisZ, quaternion }
  })
}

/**
 * Floors given as data ({ name, cx, cz, hx, hz, ax, top }), as the WALK_
 * surfaces parseObstacles makes: something to stand on that blocks nobody,
 * its top where the feet go. The monorail's platform and the steps to its
 * lift (lib/world/monorail.js).
 */
export function walkablesFromFloors(floors) {
  return collidersFromBlocks(floors.map((f) => ({ ...f, y0: f.top - 0.1, y1: f.top })), 0, 'WALK')
}

/**
 * Finds the closest point on an AABB surface to a given point.
 * Returns the clamped point inside/on the box boundary.
 * 
 * @param {THREE.Box3} box 
 * @param {THREE.Vector3} point 
 * @param {THREE.Vector3} [target] - Optional target vector to store result
 * @returns {THREE.Vector3}
 */
export function closestPointOnAABB(box, point, target = new THREE.Vector3()) {
  return target.copy(point).clamp(box.min, box.max)
}

/**
 * Checks if a point is inside an AABB (XZ-plane only check, ignoring Y).
 * Useful for NO_GO zones modeled as flat planes.
 * 
 * @param {THREE.Box3} box 
 * @param {THREE.Vector3} point 
 * @returns {boolean}
 */
export function isInsideXZ(box, point) {
  return (
    point.x >= box.min.x &&
    point.x <= box.max.x &&
    point.z >= box.min.z &&
    point.z <= box.max.z
  )
}

/**
 * Finds the nearest point on the AABB surface (pushes point outward).
 * Returns the direction and distance needed to escape.
 * 
 * @param {THREE.Box3} box 
 * @param {THREE.Vector3} point 
 * @returns {{ direction: THREE.Vector3, distance: number } | null}
 */
export function computePushOutVector(box, point) {
  // Find distances to each face
  const distToMinX = point.x - box.min.x
  const distToMaxX = box.max.x - point.x
  const distToMinZ = point.z - box.min.z
  const distToMaxZ = box.max.z - point.z

  // Find minimum distance (which face is closest)
  const distances = [
    { axis: 'x', dir: -1, dist: distToMinX },
    { axis: 'x', dir: 1, dist: distToMaxX },
    { axis: 'z', dir: -1, dist: distToMinZ },
    { axis: 'z', dir: 1, dist: distToMaxZ },
  ]

  distances.sort((a, b) => a.dist - b.dist)
  const nearest = distances[0]

  const direction = new THREE.Vector3()
  if (nearest.axis === 'x') {
    direction.x = nearest.dir
  } else {
    direction.z = nearest.dir
  }

  return {
    direction,
    distance: nearest.dist,
  }
}

